import { inject, Injectable, Signal, signal } from '@angular/core';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';

import { iColorsTheme, iThemeTypes, themes } from '../interfaces/themes.interface';
import { FaviconService } from './favicon.service';
import { IAuthConfig, ISystemConfigRegisterPayload } from '@shared/system-config/system-config.interface';
import { SystemConfigService } from '@shared/system-config/system-config.service';
import { FileService } from './file.service';
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private titleService = inject(Title);
  private faviconService = inject(FaviconService);
  private systemConfigService = inject(SystemConfigService);
  private fileService = inject(FileService);
  private sanitizer = inject(DomSanitizer);

  logo = signal<SafeUrl | null>(null);
  favicon = signal<SafeUrl | null>(null);
  colors = signal<iColorsTheme | null>(null);
  reference = signal<string | null>(null);
  permissions = signal<IAuthConfig[]>([]);

  setReference(reference: string) {
    sessionStorage.setItem('reference', reference);
    this.reference.set(reference);
  }

  getReference(): Signal<string | null> {
    const reference = sessionStorage.getItem('reference') as string;
    if (!this.reference() && reference) {
      this.reference.set(reference);
    }
    return this.reference.asReadonly();
  }


  setLogo(logo: SafeUrl) {
    this.logo.set(logo);
  }

  getLogo(): Signal<SafeUrl | null> {
    return this.logo.asReadonly();
  }

  setFavicon(favicon: SafeUrl) {
    this.favicon.set(favicon);
  }

  getFavicon(): Signal<SafeUrl | null> {
    return this.favicon.asReadonly();
  }

  setVariable(name: string, value: string): void {
    document.documentElement.style.setProperty(name, value);
  }

  setColors(colors: iColorsTheme) {
    this.colors.set(colors);
  }

  getColors(): Signal<iColorsTheme | null> {
    return this.colors.asReadonly();
  }

  setThemeByThemeName(themeName: iThemeTypes) {
    const reference = sessionStorage.getItem('reference') as string;
    if (!reference) {
      const theme = themes.get(themeName);
      if (theme) {
        this.setVariables(theme.colors);
        this.titleService.setTitle(theme.title);
        this.faviconService.changeFavicon(theme.favicon);
      }
    } else {
      this.setReference(reference);
      const theme = JSON.parse(sessionStorage.getItem('theme') as string) as ISystemConfigRegisterPayload;
      if (theme) {
        if (theme.auth?.length) {
          this.setPermissions(theme.auth as IAuthConfig[]);
        }
        this.loadLogo(theme.properties.logo);
        this.loadFavicon(theme.properties.favicon);
        this.setThemeBySystemConfig(theme);
      } else {
        this.systemConfigService.findByReference(reference).subscribe((res) => {
          if (res) {
            const systemConfig = res as unknown as ISystemConfigRegisterPayload;
            this.loadLogo(systemConfig.properties.logo);
            this.loadFavicon(systemConfig.properties.favicon);
            sessionStorage.setItem('theme', JSON.stringify(systemConfig));
            this.setThemeBySystemConfig(systemConfig);
          }
        });
      }
    }
  }

  setPermissions(permissions: IAuthConfig[]) {
    this.permissions.set(permissions);
  }

  getPermissions(): Signal<IAuthConfig[]> {
    return this.permissions.asReadonly();
  }

  loadLogo(logoPath: string) {
    this.fileService.getFileWithAuthorization(logoPath).subscribe((res: Blob) => {
      const objectURL = URL.createObjectURL(res);
      this.setLogo(this.sanitizer.bypassSecurityTrustUrl(objectURL));
    });
  }

  loadFavicon(faviconPath: string) {
    this.fileService.getFileWithAuthorization(faviconPath).subscribe((res: Blob) => {
      const objectURL = URL.createObjectURL(res);
      this.setFavicon(this.sanitizer.bypassSecurityTrustUrl(objectURL));
    });
  }

  setThemeBySystemConfig(systemConfig: ISystemConfigRegisterPayload) {
    this.setVariables(systemConfig.properties?.colors);
    this.titleService.setTitle(systemConfig.properties.title);
    this.faviconService.changeFavicon(systemConfig.properties.favicon);
  }

  setVariables(colors: iColorsTheme) {
    this.setVariable('--primary', colors.primary);
    this.setVariable('--primary-dark', colors.primaryDark);
    this.setVariable('--primary-light', colors.primaryLight);
    this.setVariable('--primary-200', colors.primary200);
  }
}
